import type { WidgetDataBag } from '../patch-helpers';

/**
 * The per-type element template — what the editor fills in that the model must not.
 *
 * A form element carries three kinds of field: what the user writes (`label`, `placeholder`,
 * `required`), what follows from the KIND of field (`htmlInputType`, `autocompleteType`, the system
 * `variable` for email/phone), and what only exists once a human has picked something in the panel
 * (`crmContactVariable`, `crmDealVariable`, the six messenger `*Variable` bags). Only the first kind
 * is in the AI surface; this table owns the second; the third stays null.
 *
 * **Why a table here rather than an import of `defaultFormElements`.** The host's own map
 * (`common/constants/form-element.const.ts`) is the source these values are copied from, and it
 * would be the obvious thing to import — `packages/*` already imports `@common/enums` in a few
 * places, so the boundary allows it. It is not imported because its `label`/`placeholder` are
 * **i18n keys**, not text (`label: controlsLabels.input`, `placeholder: 'control_input_placeholder'`),
 * and the editor translates them at the moment it creates the element
 * (`FormControlComponent.addInput` → `translateService.instant(...)`). `apps/mcp-backend` has no
 * TranslateService and no locale to translate for, so importing the map would persist
 * `control_input_placeholder` as the visible placeholder of a published form. What this layer needs
 * from that map is the derived triple, and that is what is mirrored — deliberately by hand, with the
 * host file named here so the pair can be re-checked.
 *
 * Types the host map has that are NOT here, each for a reason the model can't work around:
 * `hidden` (its whole point is a value only the page author knows, and an empty one is a widget
 * error), `file` (an upload widget), `password`, `promocode` (meaningless without
 * `additionalFormSettings.promoCodeSettings` and a real promo-code id — stage A), and `question`,
 * which the host map does not define at all (`sp-form-element-settings.component.ts` deletes it from
 * its own copy).
 */
export const FORM_ELEMENT_TYPES = [
    'email',
    'name',
    'phone',
    'input',
    'textarea',
    'address',
    'date',
    'url',
    'number',
    'checkbox',
    'radio',
    'select',
] as const;

export type FormElementTypeName = (typeof FORM_ELEMENT_TYPES)[number];

/**
 * Element kinds exempt from the "needs a CRM variable" widget error — see
 * `FormControlComponent.getWidgetValidationErrors`, which returns early for email/phone and skips
 * the check for textarea. Every other kind shows `variable_required` in the save-confirm modal until
 * a human maps it, and no tool can do that mapping (the ids come from the account's CRM).
 *
 * Exported because the tool descriptions quote it: a model that adds a `name` field should be able
 * to tell the user what is left to do, rather than leaving them to discover it at publish time.
 */
export const CRM_VARIABLE_EXEMPT_TYPES: readonly FormElementTypeName[] = ['email', 'phone', 'textarea'];

/** Mirrors `baseElement` in the host map: everything every kind carries, before the kind's extras. */
const BASE_ELEMENT: WidgetDataBag = {
    label: '',
    protected: false,
    required: false,
    isDynamicPlaceholderEnabled: false,
    variable: null,
    crmContactVariable: null,
    crmDealVariable: null,
    telegramVariable: null,
    instagramVariable: null,
    viberVariable: null,
    messengerVariable: null,
    whatsappVariable: null,
    tiktokVariable: null,
    value: '',
};

/**
 * The kind-derived extras, field for field as the host map spells them.
 *
 * Two absences are copied deliberately rather than tidied up: `textarea` has **no**
 * `htmlInputType` (the host's `textareaElement` spreads the base element, not the input one), and
 * `checkbox`/`radio`/`select` have **no** `autocompleteType` for the same reason. Filling them in
 * "for consistency" would make an AI-authored element differ from a hand-made one in the persisted
 * JSON, which is the whole class of bug this table exists to avoid.
 */
const ELEMENT_EXTRAS: Readonly<Record<FormElementTypeName, WidgetDataBag>> = {
    input: { htmlInputType: 'text', autocompleteType: 'off' },
    name: { htmlInputType: 'text', autocompleteType: 'name' },
    email: {
        htmlInputType: 'email',
        autocompleteType: 'email',
        variable: 'email',
        required: true,
        protected: true,
    },
    phone: {
        htmlInputType: 'tel',
        autocompleteType: 'tel',
        variable: 'phone',
        required: true,
        protected: true,
    },
    address: { htmlInputType: 'text', autocompleteType: 'street-address' },
    date: { htmlInputType: 'date', autocompleteType: 'off' },
    url: { htmlInputType: 'url', autocompleteType: 'url' },
    number: { htmlInputType: 'number', autocompleteType: 'off' },
    textarea: { autocompleteType: 'off', crmCommentPlace: 'crmContactComment' },
    checkbox: { htmlInputType: 'checkbox' },
    radio: { htmlInputType: 'radio', options: [] },
    select: { htmlInputType: 'select', options: [] },
};

/** Kinds that render a list of choices — the only ones `options` means anything for. */
const OPTION_BEARING_TYPES: readonly FormElementTypeName[] = ['radio', 'select'];

function isOptionBearingType(type: FormElementTypeName): boolean {
    return OPTION_BEARING_TYPES.includes(type);
}

export type FormElementTemplateOverrides = {
    label?: string;
    placeholder?: string;
    required?: boolean;
    options?: readonly string[];
};

/**
 * Builds one persisted element from its kind plus the three fields a caller may set.
 *
 * `email` and `phone` keep the template's `protected: true`/`required: true` even against an
 * explicit `required: false`: `protected` is what makes the editor refuse to delete the field, and a
 * protected-but-optional email is a state the panel cannot produce. Every other kind takes the
 * caller's `required` as given.
 *
 * `options` are accepted as plain strings and expanded into the persisted
 * `{ value, label, selected }` triples — a select whose `options` stayed `[]` (the template default)
 * renders an empty dropdown, which is the silent-nonsense outcome this widget is prone to.
 */
export function buildFormElement(
    type: FormElementTypeName,
    overrides: FormElementTemplateOverrides = {}
): WidgetDataBag {
    const extras = ELEMENT_EXTRAS[type];
    const element: WidgetDataBag = { ...BASE_ELEMENT, ...extras, type };

    if (overrides.label !== undefined) {
        element['label'] = overrides.label;
    }

    if (overrides.placeholder !== undefined) {
        element['placeholder'] = overrides.placeholder;
    }

    // Only email and phone are pinned — `CRM_VARIABLE_EXEMPT_TYPES` also lists `textarea`, which is
    // exempt from a different rule (the CRM-variable one) and takes the caller's `required` as given.
    if (overrides.required !== undefined && type !== 'email' && type !== 'phone') {
        element['required'] = overrides.required;
    }

    if (overrides.options && isOptionBearingType(type)) {
        element['options'] = overrides.options.map((option) => ({
            value: option,
            label: option,
            selected: false,
        }));
    }

    return element;
}
